import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLazyGetAcademyRedirectUrlQuery } from "../../../services/Academy/academyAPI";
import { storedLocale } from "../../Authentication/util";
import { openAcademyWindow, trackAcademyClick } from "../useAcademyLink";
import { ROUTE_PATHS } from "../../../common/enums/routing-enums";
import { IconErrorGeneral } from "../../../common/icons/IconErrorGeneral/IconErrorGeneral";
import LeafButton, { Variant } from "../../../common/leaf/LeafButton/LeafButton";
import { IconArrowLink } from "../../../common/icons/IconArrowLink/IconArrowLink";
import defaultContent from "../../../content/academy";
import "./AcademyAuthPage.scss";

interface LocationState {
  from?: string;
}

export const AcademyAuthPage = () => {
  const { t } = useTranslation(["academy"]);
  const navigate = useNavigate();
  const { state } = useLocation();
  const hasTriggered = useRef(false);
  const [progress, setProgress] = useState("0%");
  const [hasError, setHasError] = useState(false);

  const Locale = storedLocale();

  const { from = ROUTE_PATHS.PERFORMANCE_DASHBOARD } = (state as LocationState) || {};
  const returnPath = from;

  const [triggerAuthQuery] = useLazyGetAcademyRedirectUrlQuery();

  useEffect(() => {
    if (hasTriggered.current) return;
    hasTriggered.current = true;

    const fetchAuthUrl = async () => {
      trackAcademyClick();
      setProgress("10%");

      try {
        setProgress("50%");
        const result = await triggerAuthQuery({ Locale }).unwrap();
        setProgress("100%");

        // Brief delay to show 100% before opening window
        await new Promise((resolve) => setTimeout(resolve, 800));

        if (result.redirectUrl) {
          openAcademyWindow(result.redirectUrl);
        }
        navigate(returnPath, { replace: true });
      } catch {
        setHasError(true);
      }
    };

    fetchAuthUrl();
  }, [triggerAuthQuery, Locale, navigate, returnPath]);

  if (hasError) {
    return (
      <div className="academy-auth-page academy-auth-page--error">
        <div className="academy-auth-page__icon">
          <IconErrorGeneral />
        </div>
        <div className="academy-auth-page__error-message">{t("error", defaultContent.error)}</div>
        <LeafButton
          variant={Variant.SOLID}
          clickAction={() => navigate(returnPath)}
          id="academy-error-back-button"
          Icon={<IconArrowLink iconColor="white" />}
        >
          {t("goBack", defaultContent.goBack)}
        </LeafButton>
      </div>
    );
  }

  return (
    <div className="academy-auth-page">
      <p className="academy-auth-page__message">{t("loading", defaultContent.loading)}</p>
      <div className="academy-auth-page__progress">
        <div className="academy-auth-page__bar" style={{ width: progress }}></div>
      </div>
    </div>
  );
};
